import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import NotificationService from 'services/notification';
import { Notification } from './types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  fcmToken: string | null;
  notificationsEnabled: boolean;

  // Actions
  loadNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearOldNotifications: (daysOld?: number) => void;
  addNotification: (notification: Notification) => void;
  setNotifications: (notifications: Notification[]) => void;
  clearError: () => void;
  setFCMToken: (token: string | null) => void;
  setNotificationsEnabled: (enabled: boolean) => void;
  handleIncomingNotification: (payload: any) => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    persist(
      (set, get) => ({
        notifications: [],
        unreadCount: 0,
        isLoading: false,
        error: null,
        fcmToken: null,
        notificationsEnabled: false,

        loadNotifications: async () => {
          set({ isLoading: true, error: null });
          try {
            const response = await NotificationService.find({
              page: 1,
              limit: 100,
              orderBy: { createdAt: 'desc' },
              fetchPolicy: 'cache-first',
            });

            const notifications = (response?.data || []) as Notification[];
            const unreadCount = notifications.filter(n => !n.isRead).length;

            set({
              notifications,
              unreadCount,
              isLoading: false,
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load notifications',
              isLoading: false,
            });
            console.error('Error loading notifications:', error);
          }
        },

        markAsRead: async (id) => {
          set({ error: null });
          try {
            await NotificationService.markAsRead({ notificationId: id });

            set((state) => ({
              notifications: state.notifications.map((notification) =>
                notification.id === id
                  ? { ...notification, isRead: true }
                  : notification
              ),
              unreadCount: Math.max(0, state.unreadCount - 1),
            }));
          } catch (error: any) {
            set({
              error: error.message || 'Failed to mark notification as read',
            });
            throw error;
          }
        },

        markAllAsRead: async () => {
          set({ error: null });
          try {
            await NotificationService.markAllAsRead();

            set((state) => ({
              notifications: state.notifications.map((notification) => ({
                ...notification,
                isRead: true,
              })),
              unreadCount: 0,
            }));
          } catch (error: any) {
            set({
              error: error.message || 'Failed to mark all notifications as read',
            });
            throw error;
          }
        },

        deleteNotification: async (id) => {
          set({ error: null });
          try {
            await NotificationService.delete({ id });

            set((state) => {
              const notification = state.notifications.find(n => n.id === id);
              const wasUnread = notification && !notification.isRead;

              return {
                notifications: state.notifications.filter(n => n.id !== id),
                unreadCount: wasUnread
                  ? Math.max(0, state.unreadCount - 1)
                  : state.unreadCount,
              };
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to delete notification',
            });
            throw error;
          }
        },

        clearOldNotifications: (daysOld = 30) => {
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - daysOld);

          set((state) => {
            const filtered = state.notifications.filter(
              (notification) =>
                new Date(notification.createdAt) > cutoffDate
            );

            const unreadCount = filtered.filter(n => !n.isRead).length;

            return {
              notifications: filtered,
              unreadCount,
            };
          });
        },

        addNotification: (notification) => {
          set((state) => ({
            notifications: [notification, ...state.notifications],
            unreadCount: !notification.isRead
              ? state.unreadCount + 1
              : state.unreadCount,
          }));
        },

        setNotifications: (notifications) => {
          const unreadCount = notifications.filter(n => !n.isRead).length;
          set({ notifications, unreadCount });
        },

        clearError: () => {
          set({ error: null });
        },

        setFCMToken: (token) => {
          set({ fcmToken: token });
        },

        setNotificationsEnabled: (enabled) => {
          set({ notificationsEnabled: enabled });
        },

        handleIncomingNotification: (payload) => {
          const notification: Notification = {
            id: payload.messageId || Date.now().toString(),
            title: payload.notification?.title || 'Thông báo mới',
            body: payload.notification?.body,
            content: payload.notification?.body,
            type: payload.data?.type || 'info',
            createdAt: new Date().toISOString(),
            isRead: false,
            data: payload.data || {}
          };

          set((state) => ({
            notifications: [notification, ...state.notifications],
            unreadCount: state.unreadCount + 1
          }));

          // Handle navigation action if specified
          if (payload.data?.action === 'navigate' && payload.data?.url) {
            if (!document.hidden) {
              window.location.href = payload.data.url;
            }
          }
        },
      }),
      {
        name: 'notification-storage',
        partialize: (state) => ({
          notifications: state.notifications.slice(0, 50), // Store only latest 50
          unreadCount: state.unreadCount,
          notificationsEnabled: state.notificationsEnabled,
        }),
      }
    ),
    { name: 'notification-store' }
  )
);